import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddDeviceToLocationModal from "../../components/modal/AddDeviceToLocationModal";
import EditDeviceModal from "../../components/modal/EditDeviceModal";
import DeleteConfirmationModal from "../../components/modal/DeleteConfirmationModal";
import { useGetAllDevices, useGetAllLocations, useDeleteDevice } from "../../queries/admin";
import {
  PlusIcon,
  EllipsisVerticalIcon,
  InformationCircleIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { classNames } from "../../utils/string";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { useQueryClient } from "@tanstack/react-query";
import ImageAvatar from "../../components/ui/ImageAvatar";
import { useIsAdmin } from "../../utils/auth";

function getTimeAgo(dateStr: string | Date | null | undefined): string {
  if (!dateStr) return "No data";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}min ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

// Connectivity is the only thing the badge conveys: a device is Online when it
// has sent data within 3x its data rate (isOnline, computed server-side), and
// Offline otherwise — including registered devices that have never reported.
function getStatusBadge(isOnline?: boolean) {
  if (isOnline) {
    return (
      <span className="inline-flex items-center rounded-full bg-green-50 dark:bg-green-900/20 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-400">
        Online
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-red-50 dark:bg-red-900/20 px-2 py-0.5 text-xs font-medium text-red-700 dark:text-red-400">
      Offline
    </span>
  );
}

function DeviceRow({
  device,
  locationName,
  onInfo,
  onEdit,
  onDelete,
  onClick,
  canManage,
}: {
  device: any;
  locationName: string;
  onInfo: (device: any) => void;
  onEdit: (device: any) => void;
  onDelete: (deviceId: string, deviceName: string) => void;
  onClick: (device: any) => void;
  canManage: boolean;
}) {
  return (
    <div
      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-6 py-4 cursor-pointer hover:shadow-sm transition-shadow"
      onClick={() => onClick(device)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          <ImageAvatar imageKey={device.imageKey} name={device.name} />
          <div className="grid grid-cols-4 gap-4 flex-1 items-center">
            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {device.name}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {locationName || device.location || "\u2014"}
            </span>
            <span className="text-sm">{getStatusBadge(device.isOnline)}</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {getTimeAgo(device.lastDataReceivedAt)}
            </span>
          </div>
        </div>
        <Menu
          as="div"
          className="relative ml-4"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <MenuButton className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
            <EllipsisVerticalIcon className="h-5 w-5" />
          </MenuButton>
          <Transition
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <MenuItems className="absolute right-0 z-40 mt-2 w-40 origin-top-right rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black/5 dark:ring-gray-700 focus:outline-none">
              <div className="py-1">
                <MenuItem>
                  {({ focus }) => (
                    <button
                      onClick={() => onInfo(device)}
                      className={classNames(
                        focus ? "bg-gray-50 dark:bg-gray-700" : "",
                        "flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer",
                      )}
                    >
                      Info
                      <InformationCircleIcon className="h-4 w-4" />
                    </button>
                  )}
                </MenuItem>
                {canManage && (
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        onClick={() => onEdit(device)}
                        className={classNames(
                          focus ? "bg-gray-50 dark:bg-gray-700" : "",
                          "flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer",
                        )}
                      >
                        Edit
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    )}
                  </MenuItem>
                )}
                {canManage && (
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        onClick={() => onDelete(device.id, device.name)}
                        className={classNames(
                          focus ? "bg-gray-50 dark:bg-gray-700" : "",
                          "flex items-center justify-between w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 cursor-pointer",
                        )}
                      >
                        Delete
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </MenuItem>
                )}
              </div>
            </MenuItems>
          </Transition>
        </Menu>
      </div>
    </div>
  );
}

export default function DevicesPage() {
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const { data: devices } = useGetAllDevices();
  const { data: locations } = useGetAllLocations();
  const { mutate: deleteDevice } = useDeleteDevice();
  const queryClient = useQueryClient();

  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [showEditDeviceModal, setShowEditDeviceModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState<any>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalConfig, setDeleteModalConfig] = useState<{
    title: string;
    itemName: string;
    itemType: string;
    onConfirm: () => void;
  } | null>(null);

  // Build a map of locationId -> locationName
  const locationMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (locations) {
      locations.forEach((loc: any) => {
        map[loc.id] = loc.name;
      });
    }
    return map;
  }, [locations]);

  // Count devices per location
  const deviceCountByLocation = useMemo(() => {
    const counts: Record<string, number> = {};
    if (devices) {
      devices.forEach((d: any) => {
        if (d.locationId) {
          counts[d.locationId] = (counts[d.locationId] || 0) + 1;
        }
      });
    }
    return counts;
  }, [devices]);

  const isAllSelected = selectedLocationIds.size === 0;

  const toggleLocation = (locationId: string) => {
    setSelectedLocationIds((prev) => {
      const next = new Set(prev);
      if (next.has(locationId)) {
        next.delete(locationId);
      } else {
        next.add(locationId);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedLocationIds(new Set());
  };

  const filteredDevices = useMemo(() => {
    if (!devices) return [];
    let filtered = devices;

    // Filter by selected locations
    if (!isAllSelected) {
      filtered = filtered.filter((device: any) => selectedLocationIds.has(device.locationId));
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (device: any) =>
          device.name?.toLowerCase().includes(term) ||
          device.identifier?.toLowerCase().includes(term) ||
          device.status?.toLowerCase().includes(term) ||
          device.location?.toLowerCase().includes(term) ||
          (device.locationId && locationMap[device.locationId]?.toLowerCase().includes(term)),
      );
    }

    return filtered;
  }, [devices, searchTerm, locationMap, selectedLocationIds, isAllSelected]);

  const handleDeleteDevice = (deviceId: string, deviceName: string) => {
    setDeleteModalConfig({
      title: "Delete Device",
      itemName: deviceName,
      itemType: "device",
      onConfirm: () => {
        deleteDevice(deviceId, {
          onSuccess: () => {
            toast.success("Device deleted successfully");
            queryClient.invalidateQueries({ queryKey: ["devices"] });
            setShowDeleteModal(false);
            setDeleteModalConfig(null);
          },
          onError: (error: any) => {
            toast.error(error?.response?.data?.errorMessage || "Failed to delete device");
            setShowDeleteModal(false);
            setDeleteModalConfig(null);
          },
        });
      },
    });
    setShowDeleteModal(true);
  };

  const handleEditDevice = (device: any) => {
    setEditingDevice(device);
    setShowEditDeviceModal(true);
  };

  const handleInfoDevice = (device: any) => {
    navigate(`/devices/${device.id}`);
  };

  const handleDeviceClick = (device: any) => {
    navigate(`/devices/${device.id}`);
  };

  return (
    <>
      <div className="flex-1 flex flex-col gap-6">
        {/* Header */}
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Devices</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage device connectivity and deployment across locations
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowAddDeviceModal(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shrink-0"
            >
              <PlusIcon className="h-4 w-4" />
              New Device
            </button>
          )}
        </div>

        {/* Location chips */}
        <div>
          <div className="flex flex-wrap items-center gap-2">
            {/* All chip */}
            <button
              onClick={selectAll}
              className={classNames(
                isAllSelected
                  ? "bg-primary text-white border-primary"
                  : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700",
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors cursor-pointer",
              )}
            >
              All
              <span
                className={classNames(
                  isAllSelected
                    ? "bg-white/20 text-white"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400",
                  "rounded-full px-1.5 py-0.5 text-xs font-medium",
                )}
              >
                {devices?.length || 0}
              </span>
            </button>

            {/* Location chips */}
            {locations?.map((loc: any) => {
              const isSelected = selectedLocationIds.has(loc.id);
              const count = deviceCountByLocation[loc.id] || 0;
              return (
                <button
                  key={loc.id}
                  onClick={() => toggleLocation(loc.id)}
                  className={classNames(
                    isSelected
                      ? "bg-primary text-white border-primary"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700",
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors cursor-pointer",
                  )}
                >
                  {loc.name}
                  <span
                    className={classNames(
                      isSelected
                        ? "bg-white/20 text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400",
                      "rounded-full px-1.5 py-0.5 text-xs font-medium",
                    )}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center gap-4 w-full">
          <div className="relative flex-1 max-w-[50%]">
            <input
              type="text"
              placeholder="Search Devices"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-10 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <Icon
              icon="mdi:magnify"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            />
          </div>
        </div>

        {/* Device list */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 flex flex-col space-y-3">
            {filteredDevices && filteredDevices.length > 0 ? (
              filteredDevices.map((device: any) => (
                <DeviceRow
                  key={device.id}
                  device={device}
                  locationName={locationMap[device.locationId] || ""}
                  onInfo={handleInfoDevice}
                  onEdit={handleEditDevice}
                  onDelete={handleDeleteDevice}
                  onClick={handleDeviceClick}
                  canManage={isAdmin}
                />
              ))
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center bg-white dark:bg-[#111B21] rounded-lg border border-gray-200 dark:border-gray-700">
                <img
                  src="/sidenav/devices.svg"
                  alt=""
                  className="mx-auto h-20 w-20 opacity-60 dark:invert"
                />
                <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                  {isAllSelected ? "No devices" : "No devices for selected location(s)"}
                </h3>
                <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
                  {isAllSelected
                    ? "Get started by adding a new device."
                    : "Try selecting a different location or add a device."}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showAddDeviceModal && (
        <AddDeviceToLocationModal
          isOpen={showAddDeviceModal}
          onClose={() => setShowAddDeviceModal(false)}
        />
      )}

      {showEditDeviceModal && (
        <EditDeviceModal
          isOpen={showEditDeviceModal}
          onClose={() => {
            setShowEditDeviceModal(false);
            setEditingDevice(null);
          }}
          device={editingDevice}
        />
      )}

      {showDeleteModal && deleteModalConfig && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteModalConfig(null);
          }}
          onConfirm={deleteModalConfig.onConfirm}
          title={deleteModalConfig.title}
          itemName={deleteModalConfig.itemName}
          itemType={deleteModalConfig.itemType}
        />
      )}
    </>
  );
}
